import React, { useMemo } from "react";
import { Line } from "@react-three/drei";
import * as THREE from "three";
import { floorplannerStore } from "../../store/floorplannerStore";
import { SymbolType } from "../../types/floorplannerTypes";
import {
  createProjectedShapePrimitiveFootprintPolygon,
  shouldShowShapePrimitiveProjection,
} from "../../features/floorplan3d/symbols/primitives/shapes/shapePrimitiveGeometry";
import { CANVAS_ACCENT_COLOR_HEX } from "../../constants/brandColors";
import { isSymbolAbovePlanCutPlane } from "../../utils/planCutPlane";

type ShapeProjectionOverlayProps = {
  symbol: SymbolType;
  z?: number;
  primary?: boolean;
};

const ShapeProjectionOverlay: React.FC<ShapeProjectionOverlayProps> = ({
  symbol,
  z = 0.006,
  primary = true,
}) => {
  const points = useMemo(() => {
    // Overhead (above cut plane) uses clear dashed plan strokes instead of a ghost overlay.
    if (isSymbolAbovePlanCutPlane(symbol)) return [];
    if (!shouldShowShapePrimitiveProjection(symbol)) return [];
    return (
      createProjectedShapePrimitiveFootprintPolygon(
        symbol,
        Array.from(floorplannerStore.wallsMap.values()),
      ) ?? []
    );
  }, [
    symbol,
    symbol.position.x,
    symbol.position.y,
    symbol.rotation,
    symbol.threeD?.height,
    symbol.threeD?.baseElevation,
    symbol.threeD?.placementMode,
    symbol.threeD?.rotation3D?.pitch,
    symbol.threeD?.rotation3D?.roll,
    symbol.threeD?.wallMount?.wallId,
    symbol.threeD?.wallMount?.side,
    symbol.threeD?.wallMount?.offset,
    symbol.threeD?.wallMount?.distanceFromWall,
  ]);

  const shape = useMemo(() => {
    if (points.length < 3) return null;
    const nextShape = new THREE.Shape();
    nextShape.moveTo(points[0].x, points[0].y);
    points.slice(1).forEach((point) => nextShape.lineTo(point.x, point.y));
    nextShape.closePath();
    return nextShape;
  }, [points]);

  if (!shape || points.length < 3) return null;

  const linePoints: [number, number, number][] = points
    .concat(points[0])
    .map((point) => [point.x, point.y, z + 0.001]);
  const fillColor = symbol.fillColor ?? CANVAS_ACCENT_COLOR_HEX;
  const lineColor = symbol.selected ? CANVAS_ACCENT_COLOR_HEX : (symbol.lineColor ?? CANVAS_ACCENT_COLOR_HEX);

  return (
    <group>
      <mesh position={[0, 0, z]} raycast={() => null}>
        <shapeGeometry args={[shape]} />
        <meshBasicMaterial
          color={fillColor}
          transparent
          opacity={primary ? 0.72 : 0.18}
          depthWrite={false}
          side={THREE.DoubleSide}
        />
      </mesh>
      <Line
        points={linePoints}
        color={lineColor}
        lineWidth={primary ? 1.5 : 1}
        dashed={!primary}
        dashSize={0.12}
        gapSize={0.08}
        raycast={() => null}
      />
    </group>
  );
};

export default ShapeProjectionOverlay;
